/**
 * Radio playback. OpenAI clips are fetched as soon as a message is queued (at most two at a
 * time) and played strictly in order, so a readback follows its clearance without a gap.
 * Each clip is trimmed of leading/trailing silence, its long pauses shortened, faded at the edges (no clicks), and run
 * through a VHF-style chain: band-pass, light saturation, compression, and a short squelch
 * burst at key-up. `?radiodebug=1` logs every clip's source, size, duration and latency.
 */
import type { RadioMessage } from '@atc/sim';
import { attribution } from './attribution';

/**
 * Longest pause kept inside a clip. The model pauses at commas for up to 0.7 s, which radio does not.
 * Measured: capping at 90 ms adds about 0.6 syllables a second. ASSUMPTION: 110 ms still leaves
 * an audible break between items.
 */
export const MAX_PAUSE_S = 0.11;
/** Below this peak over a 10 ms window, the clip counts as pausing (keeps breaths and stop consonants). */
const PAUSE_LEVEL = 0.03;
const FADE_S = 0.008;
const SILENCE = 0.012;
const MAX_IN_FLIGHT = 2;
/** ASSUMPTION: a browser voice that has not started within this long is stuck, not slow. */
const BROWSER_START_TIMEOUT_MS = 2000;
/** ASSUMPTION: long enough for Chrome to settle a cancel before the retry is spoken. */
const BROWSER_RETRY_DELAY_MS = 250;
/**
 * macOS novelty and effect voices (singing, whispering, robots). They are no radio voice, and Chrome
 * leaves some of them silent: a callsign that hashed to "Bells" was never heard at all.
 */
const NOVELTY_VOICES = new Set(['Albert', 'Bad News', 'Bahh', 'Bells', 'Boing', 'Bubbles', 'Cellos', 'Deranged', 'Fred', 'Good News', 'Hysterical', 'Jester', 'Junior', 'Kathy', 'Organ', 'Pipe Organ', 'Princess', 'Ralph', 'Superstar', 'Trinoids', 'Whisper', 'Wobble', 'Zarvox']);
/** Voices that never started in this page; calls that would use one take the system default. */
const brokenVoices = new Set<string>();
const DEBUG = typeof location !== 'undefined' && new URLSearchParams(location.search).has('radiodebug');
let primedContext: AudioContext | null = null;
let primedExpiry: ReturnType<typeof setTimeout> | null = null;

/** Call synchronously from the Start button so later async loading does not lose user activation. */
export function primeRadioPlayback(): void {
  if (typeof AudioContext === 'undefined') return;
  if (!primedContext || primedContext.state === 'closed') primedContext = new AudioContext();
  void primedContext.resume();
  if (primedExpiry) clearTimeout(primedExpiry);
  const context = primedContext;
  primedExpiry = setTimeout(() => {
    if (primedContext !== context) return;
    primedContext = null; primedExpiry = null;
    void context.close();
  }, 30000);
}

/**
 * English dialects a browser voice can stand in for, per accent key (sim/src/airlines.ts). Browsers
 * ship no Hebrew- or German-accented English, so every other accent gets the neutral pool: hashing
 * over all English voices once gave an El Al crew an Indian English voice.
 */
const BROWSER_DIALECTS: Record<string, string[]> = { american: ['en-US'], british: ['en-GB'], australian: ['en-AU'], indian: ['en-IN'] };
const NEUTRAL_DIALECTS = ['en-US', 'en-GB'];

/** The controller keeps the first voice of its pool; each callsign hashes to one of the others. */
export function browserVoice<V extends { lang: string; name: string }>(available: readonly V[], msg: Pick<RadioMessage, 'from' | 'callsign' | 'accent'>, accents = true): V | null {
  const dialects = (accents && msg.accent && BROWSER_DIALECTS[msg.accent]) || NEUTRAL_DIALECTS;
  const inDialect = (list: string[]) => available.filter((v) => list.some((d) => v.lang.replace('_', '-').startsWith(d)));
  const pool = inDialect(dialects).length ? inDialect(dialects) : inDialect(NEUTRAL_DIALECTS).length ? inDialect(NEUTRAL_DIALECTS) : [...available];
  if (msg.from === 'atc') return pool[0] ?? null;
  const atc = inDialect(NEUTRAL_DIALECTS)[0];
  const pilots = pool.length > 1 ? pool.filter((v) => v !== atc) : pool;
  return pilots[hash(msg.callsign) % Math.max(1, pilots.length)] ?? null;
}

interface Item { msg: RadioMessage; at: number; abort: AbortController; audio?: Promise<AudioBuffer | null> }

const hash = (s: string) => [...s].reduce((n, c) => (n * 31 + c.charCodeAt(0)) >>> 0, 7);

export type NoiseLevel = 'off' | 'low' | 'normal';
/** Multiplier on the carrier hiss and its squelch tail. "low" is the default; tune it by ear. */
const NOISE: Record<NoiseLevel, number> = { off: 0, low: 1, normal: 2 };

export function createRadio(options: { enabled: boolean; provider: 'browser' | 'openai'; pace?: 'normal' | 'brisk'; noise?: NoiseLevel; accents?: boolean; onError(message: string): void; onLine?(msg: RadioMessage, onAir: boolean): void }) {
  let pace = options.pace ?? 'brisk';
  let noiseLevel: NoiseLevel = options.noise ?? 'low';
  let accents = options.accents ?? true;
  let enabled = options.enabled, provider = options.provider, sessionId: string | null = null, sessionPromise: Promise<string> | null = null;
  let generation = 0, busy = false, destroyed = false, context: AudioContext | null = primedContext, noise: AudioBuffer | null = null;
  if (context) { primedContext = null; if (primedExpiry) clearTimeout(primedExpiry); primedExpiry = null; }
  let stopPlaying: (() => void) | null = null, inFlight = 0;
  let sessionExpiresAt = 0, retryAt = 0, connectedAt = 0, speechCharacters = 0, speechRequests = 0;
  const headers = (token?: string) => ({ 'content-type': 'application/json', 'x-atc-voice': '1', ...attribution(), ...(token ? { 'x-atc-session': token } : {}) });
  const release = () => { if (sessionId) void fetch('/api/voice/session', { method: 'DELETE', headers: headers(sessionId), keepalive: true }).catch(() => {}); sessionId = null; sessionExpiresAt = connectedAt = 0; };
  const queue: Item[] = [];
  const synth = typeof speechSynthesis === 'undefined' ? null : speechSynthesis;
  const useOpenAI = () => provider === 'openai' && performance.now() >= retryAt;
  const audio = () => (context ??= new AudioContext());
  // Every click or key is a chance to start OpenAI playback's audio context inside a user gesture,
  // so the first clip (a lesson's check-in, sent the moment "Take the frequency" is clicked) is not
  // decoded into a context that the browser keeps suspended.
  const unlock = () => { if (provider === 'openai' && !destroyed) audio(); if (context?.state === 'suspended') void context.resume(); };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);

  function clear() {
    generation++; busy = false;
    for (const item of queue.splice(0)) item.abort.abort();
    stopPlaying?.(); stopPlaying = null; synth?.cancel();
  }

  function fail(err: unknown) {
    retryAt = performance.now() + 60000; release();
    options.onError(`OpenAI voice failed (${err instanceof Error ? err.message : 'unknown error'}); using browser voices for a minute.`);
  }

  function session(): Promise<string> {
    if (sessionId && performance.now() < sessionExpiresAt) return Promise.resolve(sessionId);
    sessionPromise ??= (async () => {
      release();
      const response = await fetch('/api/voice/playback', { method: 'POST', headers: headers(), signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error(`playback session ${response.status}`);
      const s = await response.json();
      sessionId = s.sessionId as string; connectedAt = performance.now(); sessionExpiresAt = connectedAt + s.maxSessionMinutes * 60000; speechCharacters = speechRequests = 0;
      return sessionId;
    })().finally(() => { sessionPromise = null; });
    return sessionPromise;
  }

  function fetchAudio(item: Item): Promise<AudioBuffer | null> {
    item.audio ??= (async () => {
      inFlight++;
      const started = performance.now();
      try {
        const token = await session();
        const response = await fetch('/api/voice/speech', { method: 'POST', headers: headers(token), body: JSON.stringify({ text: item.msg.speech, role: item.msg.from === 'atc' ? 'atc' : 'pilot', pace, voiceSeed: hash(item.msg.callsign), ...(accents && item.msg.accent ? { accent: item.msg.accent } : {}), ...(item.msg.crewStyle ? { style: item.msg.crewStyle } : {}), ...(item.msg.kind === 'emergency' ? { urgent: true } : {}) }), signal: AbortSignal.any([item.abort.signal, AbortSignal.timeout(15000)]) });
        if (!response.ok) throw new Error(`speech ${response.status}`);
        const bytes = await response.arrayBuffer();
        speechCharacters += item.msg.speech.length; speechRequests++;
        const decoded = await audio().decodeAudioData(bytes);
        const buffer = trim(audio(), decoded);
        if (DEBUG) console.info('[radio]', { source: 'openai', cache: response.headers.get('x-atc-tts-cache'), from: item.msg.from, text: item.msg.speech, bytes: bytes.byteLength, ms: Math.round(performance.now() - started), durationS: +decoded.duration.toFixed(2), trimmedS: +buffer.duration.toFixed(2), sampleRate: decoded.sampleRate });
        return buffer;
      } catch (err) {
        if (!item.abort.signal.aborted) fail(err);
        return null;
      } finally { inFlight--; prefetch(); }
    })();
    return item.audio;
  }

  function prefetch() {
    if (!enabled || destroyed || !useOpenAI()) return;
    for (const item of queue) { if (inFlight >= MAX_IN_FLIGHT) break; if (!item.audio) void fetchAudio(item); }
  }

  function play(buffer: AudioBuffer, msg: RadioMessage, done: () => void) {
    const ctx = audio();
    noise ??= whiteNoise(ctx);
    // The service already asked the model for the pace (voice/speech.mjs SPEECH_SPEED), keeping pitch;
    // speeding the buffer up here as well would only raise the voice.
    stopPlaying = playThroughRadio(ctx, buffer, msg, { rate: 1, noise: noiseLevel, hiss: noise }, () => { stopPlaying = null; done(); });
  }

  function browserSpeech(msg: RadioMessage, done: () => void) {
    if (!synth) { done(); return; }
    const run = generation;
    const available = synth.getVoices().filter((v) => v.lang.startsWith('en') && !NOVELTY_VOICES.has(v.name));
    const hashed = browserVoice(available, msg, accents);
    // A voice that has already failed falls back to the system default; the list itself stays put so
    // every other aircraft keeps the voice it started the session with.
    const picked = hashed && brokenVoices.has(hashed.name) ? null : hashed;
    // Chrome (seen on macOS) drops the first utterance of a session, or the first after one was
    // cancelled, in one of two ways: it never starts and `speaking` stays true (which would stall
    // the queue for good), or it "ends" at once without ever starting. Either way nothing was
    // heard, so the call is spoken once more after a cancel and a short beat; speaking again in the
    // same task as the cancel is dropped the same way.
    // The retry also drops the voice: one that never starts (seen with macOS voices Chrome cannot
    // drive) fails the same way every time, and every call from that callsign would be lost.
    const speak = (retry: boolean) => {
      const voice = retry ? picked : null;
      const utterance = new SpeechSynthesisUtterance(msg.speech);
      utterance.voice = voice;
      // ASSUMPTION: browser voices vary too much by system to measure; brisk is as fast as they stay clear.
      utterance.rate = pace === 'normal' ? 1.1 : msg.from === 'atc' ? 1.3 : 1.35;
      let started = false;
      const again = (why: string) => {
        clearTimeout(watchdog);
        utterance.onend = utterance.onerror = null;
        if (run !== generation) return;
        if (voice && why === 'stalled') brokenVoices.add(voice.name);
        synth.cancel();
        if (DEBUG) console.info('[radio]', { source: 'browser', [why]: msg.speech, voice: voice?.name, retry });
        if (retry) setTimeout(() => { if (run === generation) speak(false); }, BROWSER_RETRY_DELAY_MS); else done();
      };
      const watchdog = setTimeout(() => { if (!started) again('stalled'); }, BROWSER_START_TIMEOUT_MS);
      utterance.onstart = () => { started = true; };
      utterance.onend = utterance.onerror = (e?: SpeechSynthesisEvent) => {
        const error = e && 'error' in e ? (e as SpeechSynthesisErrorEvent).error : null;
        if (!started && error !== 'interrupted' && error !== 'canceled') { again('unheard'); return; }
        clearTimeout(watchdog); done();
      };
      if (DEBUG) console.info('[radio]', { source: 'browser', from: msg.from, text: msg.speech, voice: voice?.name });
      synth.speak(utterance);
    };
    speak(true);
  }

  async function pump() {
    if (destroyed || !enabled || busy) return;
    while (queue[0] && performance.now() - queue[0].at > 20000) queue.shift()!.abort.abort();
    const next = queue.shift(); if (!next) return;
    busy = true; const run = generation;
    const done = () => { options.onLine?.(next.msg, false); if (run !== generation) return; busy = false; void pump(); };
    prefetch();
    const buffer = next.audio || useOpenAI() ? await fetchAudio(next) : null;
    if (run !== generation || destroyed || !enabled) return;
    options.onLine?.(next.msg, true);
    if (buffer) play(buffer, next.msg, done); else browserSpeech(next.msg, done);
  }

  window.addEventListener('pagehide', release);
  return {
    push(msg: RadioMessage) {
      if (!enabled || destroyed) return;
      const item = { msg, at: performance.now(), abort: new AbortController() };
      const isUrgent = (m: RadioMessage) => m.kind === 'emergency' || m.kind === 'unable' || /going around|abort/i.test(m.text);
      const urgent = isUrgent(msg);
      if (urgent) {
        // Preserve ATC-before-readback order within the urgent group.
        const at = queue.findIndex((x) => !isUrgent(x.msg));
        queue.splice(at < 0 ? queue.length : at, 0, item);
      } else queue.push(item);
      if (queue.length > 8) queue.pop()!.abort.abort();
      prefetch(); void pump();
    },
    setEnabled(v: boolean, keepSession = false) { if (v === enabled) return; enabled = v; if (!v) { clear(); if (!keepSession) release(); } },
    setPace(v: 'normal' | 'brisk') { pace = v; },
    setNoise(v: NoiseLevel) { noiseLevel = v; },
    setAccents(v: boolean) { accents = v; },
    setProvider(v: 'browser' | 'openai') { clear(); release(); retryAt = 0; provider = v; },
    usage() { return { connectedSeconds: connectedAt ? Math.floor((performance.now() - connectedAt) / 1000) : 0, speechCharacters, speechRequests }; },
    interrupt() { clear(); },
    destroy() { destroyed = true; clear(); release(); window.removeEventListener('pagehide', release); window.removeEventListener('pointerdown', unlock, true); window.removeEventListener('keydown', unlock, true); void context?.close(); },
  };
}

/**
 * Drop leading/trailing near-silence (keeping 30 ms), so clips start on the first word, and shorten
 * every pause inside the clip to MAX_PAUSE_S. Cuts fall in the middle of a pause, where the signal
 * is near zero, so they do not click.
 */
export function trim(ctx: Pick<BaseAudioContext, 'createBuffer'>, buffer: AudioBuffer): AudioBuffer {
  const data = buffer.getChannelData(0), rate = buffer.sampleRate;
  let start = 0, end = data.length - 1;
  while (start < end && Math.abs(data[start]!) < SILENCE) start++;
  while (end > start && Math.abs(data[end]!) < SILENCE) end--;
  const pad = Math.floor(rate * 0.03);
  start = Math.max(0, start - pad); end = Math.min(data.length - 1, end + pad);
  if (end - start < rate * 0.1) return buffer;
  // Sample ranges to keep: all of it, less the middle of every pause longer than the cap.
  const win = Math.max(1, Math.floor(rate * 0.01)), keep = Math.floor(rate * MAX_PAUSE_S);
  const spans: [number, number][] = [];
  let from = start, quietAt = -1;
  const closePause = (at: number) => {
    if (quietAt >= 0 && at - quietAt > keep) { const cut = quietAt + Math.floor(keep / 2); spans.push([from, cut]); from = at - (keep - Math.floor(keep / 2)); }
    quietAt = -1;
  };
  for (let i = start; i <= end; i += win) {
    let peak = 0;
    for (let j = i, stop = Math.min(end + 1, i + win); j < stop; j++) peak = Math.max(peak, Math.abs(data[j]!));
    if (peak < PAUSE_LEVEL) { if (quietAt < 0) quietAt = i; } else closePause(i);
  }
  quietAt = -1; spans.push([from, end + 1]);
  const length = spans.reduce((n, [a, b]) => n + b - a, 0);
  if (length === data.length) return buffer;
  const out = ctx.createBuffer(buffer.numberOfChannels, length, rate);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const src = buffer.getChannelData(ch), dst = out.getChannelData(ch);
    let at = 0;
    for (const [a, b] of spans) { dst.set(src.subarray(a, b), at); at += b - a; }
  }
  return out;
}

/**
 * One clip through the VHF chain: band-pass, light saturation, compression, carrier hiss and a
 * squelch tail. Calls `done` once the tail has played; returns a stop function (which does not).
 */
function playThroughRadio(ctx: AudioContext, buffer: AudioBuffer, msg: Pick<RadioMessage, 'from' | 'callsign'>, o: { rate: number; noise: NoiseLevel; hiss: AudioBuffer }, done: () => void): () => void {
  void ctx.resume();
  // Each aircraft's radio sounds a little different; the controller's own set is cleanest.
  // Timbre and drive are drawn from separate hashes: with one hash the unluckiest callsign got
  // the harshest saturation and the loudest hiss at the same time, which read as noise, not radio.
  const v = msg.from === 'atc' ? 0.5 : (hash(msg.callsign) % 100) / 100;
  const vDrive = msg.from === 'atc' ? 0.5 : (hash(`${msg.callsign}#drive`) % 100) / 100;
  const t0 = ctx.currentTime + 0.05;
  const duration = buffer.duration / o.rate;

  const high = ctx.createBiquadFilter(); high.type = 'highpass'; high.frequency.value = 280 + 80 * v;
  const low = ctx.createBiquadFilter(); low.type = 'lowpass'; low.frequency.value = 2700 + 600 * v;
  const drive = ctx.createWaveShaper(); drive.curve = softClip(1.4 + 1.0 * vDrive); drive.oversample = '2x';
  const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -24; comp.ratio.value = 6; comp.attack.value = 0.003; comp.release.value = 0.12;
  const out = ctx.createGain(); out.gain.value = 0.75;
  high.connect(low).connect(drive).connect(comp).connect(out).connect(ctx.destination);

  const voice = ctx.createBufferSource(); voice.buffer = buffer; voice.playbackRate.value = o.rate;
  const env = ctx.createGain();
  env.gain.setValueAtTime(0, t0); env.gain.linearRampToValueAtTime(1, t0 + FADE_S);
  env.gain.setValueAtTime(1, t0 + duration - FADE_S); env.gain.linearRampToValueAtTime(0, t0 + duration);
  voice.connect(env).connect(high);

  // Carrier hiss under the voice, and a squelch tail when the transmitter unkeys.
  const hiss = ctx.createBufferSource(); hiss.buffer = o.hiss; hiss.loop = true;
  const hissGain = ctx.createGain(); const level = (msg.from === 'atc' ? 0.006 : 0.008 + 0.006 * v) * NOISE[o.noise];
  hissGain.gain.setValueAtTime(0, t0); hissGain.gain.linearRampToValueAtTime(level, t0 + 0.02);
  hissGain.gain.setValueAtTime(level, t0 + duration); hissGain.gain.linearRampToValueAtTime(level * 2.5, t0 + duration + 0.01); hissGain.gain.linearRampToValueAtTime(0, t0 + duration + 0.06);
  hiss.connect(hissGain).connect(high);

  const nodes = [voice, env, hiss, hissGain, high, low, drive, comp, out];
  let finished = false;
  hiss.onended = () => { if (finished) return; finished = true; for (const n of nodes) n.disconnect(); done(); };
  voice.start(t0); hiss.start(t0); hiss.stop(t0 + duration + 0.25);
  return () => { hiss.onended = null; try { voice.stop(); hiss.stop(); } catch { /* not started */ } finished = true; for (const n of nodes) n.disconnect(); };
}

let previewContext: AudioContext | null = null;
/** A generated clip as the game would play it (admin preview): trimmed, through the radio chain. */
export async function playRadioClip(bytes: ArrayBuffer, msg: Pick<RadioMessage, 'from' | 'callsign'>): Promise<void> {
  const ctx = previewContext ??= new AudioContext();
  const buffer = trim(ctx, await ctx.decodeAudioData(bytes));
  await new Promise<void>((resolve) => { playThroughRadio(ctx, buffer, msg, { rate: 1, noise: 'low', hiss: whiteNoise(ctx) }, resolve); });
}

function softClip(amount: number): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(1024);
  for (let i = 0; i < curve.length; i++) { const x = (i / (curve.length - 1)) * 2 - 1; curve[i] = Math.tanh(amount * x) / Math.tanh(amount); }
  return curve;
}

function whiteNoise(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}
